import axios from 'axios';

// Create a ref to track if we're already showing the modal to prevent multiple modals
let isShowingSessionExpiredModal = false;
let sessionExpiredCallback = null;

// Grace period (ms) after login during which we never show session-expired (token may not be in storage yet)
const LOGIN_GRACE_PERIOD_MS = 4000;
let loginGracePeriodEnd = 0;

/** True if we're within the post-login grace period - don't show session expired during this time */
const isWithinLoginGracePeriod = () => Date.now() < loginGracePeriodEnd;

// Public routes where user is NOT "inside" the app (login/signup/landing) - never show session expired here
const PUBLIC_ROUTES = ['/', '/about', '/services', '/contact', '/accessibility-statement', '/oauth/callback', '/login'];

/** True if the request URL is for login/signup/register - 401 there means bad credentials, not session expired */
const isAuthRelatedUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  const lower = url.toLowerCase();
  return (
    lower.includes('/login') ||
    lower.includes('/signup') ||
    lower.includes('/register') ||
    lower.includes('/signin') ||
    lower.includes('/oauth') ||
    lower.includes('/public')
  );
};

/** True if user is on a protected app route (inside the website) - only show session expired in that case */
const isUserOnProtectedRoute = () => {
  try {
    const pathname = (typeof window !== 'undefined' && window.location?.pathname) || '';
    return !PUBLIC_ROUTES.includes(pathname);
  } catch {
    return false;
  }
};

const TOKEN_KEYS = ['token', 'bimLiveAuthToken', 'cognito_token', 'idToken'];

/** Returns an object describing token presence in sessionStorage (values are "present" | "missing", never the actual token) */
const getTokenCheckResult = () => {
  const result = {};
  try {
    for (const key of TOKEN_KEYS) {
      const val = sessionStorage.getItem(key);
      result[key] = val != null && String(val).length > 0 ? 'present' : 'missing';
    }
  } catch (e) {
    result.error = String(e?.message || e);
  }
  return result;
};

/** Log why session-expired modal was invoked (call only when we are about to show the modal) */
const logSessionExpiredInvocation = (reason, context) => {
  const pathname = (typeof window !== 'undefined' && window.location?.pathname) || '';
  const tokenCheck = getTokenCheckResult();
  console.group('🔒 Session-expired modal invoked');
  console.log('Why:', reason);
  console.log('What caused it:', context);
  console.log('Current pathname:', pathname);
  console.log('Token lookup (sessionStorage):', tokenCheck);
  console.log('Keys checked (in order):', TOKEN_KEYS.join(', '));
  console.log('auth in sessionStorage:', sessionStorage.getItem('auth'));
  console.log('Within login grace period?:', isWithinLoginGracePeriod());
  console.groupEnd();
};

/**
 * Set the callback function to show the session expired modal
 * @param {Function} callback - Function to call when session expires
 */
export const setSessionExpiredCallback = (callback) => {
  sessionExpiredCallback = callback;
};

/**
 * Reset the session expired modal state and start a short grace period (call after user logs in).
 * During the grace period we do not show the session-expired modal, so token/storage can settle.
 */
export const resetSessionExpiredState = () => {
  isShowingSessionExpiredModal = false;
  loginGracePeriodEnd = Date.now() + LOGIN_GRACE_PERIOD_MS;
};

/**
 * Setup axios interceptors to handle 401 errors and expired tokens.
 * Session expired modal is only shown when user is inside the app (protected route)
 * and token expired or was deleted. Never shown on login/signup or when 401 is from auth API.
 */
export const setupAxiosInterceptors = () => {
  // Response interceptor to catch 401 errors
  axios.interceptors.response.use(
    (response) => {
      // If we get a successful response, reset the modal state
      if (response.status === 200 || response.status === 201) {
        isShowingSessionExpiredModal = false;
      }
      return response;
    },
    async (error) => {
      const requestUrl = error.config?.url;

      // Do NOT show session expired for 401 from login/signup/register - that's failed login
      if (isAuthRelatedUrl(requestUrl)) {
        return Promise.reject(error);
      }

      // Only show when user is inside the app (protected route), not on landing/login/signup
      if (!isUserOnProtectedRoute()) {
        return Promise.reject(error);
      }

      // Do not show during post-login grace period (token may still be establishing)
      if (isWithinLoginGracePeriod()) {
        return Promise.reject(error);
      }

      // Check if this is a 401 Unauthorized error
      if (error.response && error.response.status === 401) {
        if (!isShowingSessionExpiredModal && sessionExpiredCallback) {
          isShowingSessionExpiredModal = true;
          logSessionExpiredInvocation(
            'Response interceptor: API returned 401 Unauthorized',
            {
              source: 'response interceptor',
              requestUrl: requestUrl || error.config?.baseURL,
              status: error.response?.status,
              method: error.config?.method,
            }
          );
          sessionExpiredCallback();
        }
      }

      // Also check for token expiration in the error message
      if (error.response && error.response.data) {
        const errorMessage = error.response.data.error || error.response.data.message || '';
        const isTokenExpired =
          errorMessage.toLowerCase().includes('token expired') ||
          errorMessage.toLowerCase().includes('session expired') ||
          errorMessage.toLowerCase().includes('invalid token') ||
          errorMessage.toLowerCase().includes('not authenticated');

        if (isTokenExpired && !isShowingSessionExpiredModal && sessionExpiredCallback) {
          isShowingSessionExpiredModal = true;
          logSessionExpiredInvocation(
            'Response interceptor: error body indicated token/session expired or not authenticated',
            {
              source: 'response interceptor',
              requestUrl: requestUrl || error.config?.baseURL,
              status: error.response?.status,
              errorMessage: errorMessage.slice(0, 200),
            }
          );
          sessionExpiredCallback();
        }
      }

      return Promise.reject(error);
    }
  );

  // Request interceptor (no session-expired modal here).
  // We do NOT show session-expired when sessionStorage has no token but auth=true, because
  // Cognito/Amplify store the token in their own storage (not sessionStorage). API calls
  // may still send the token via another mechanism (e.g. Amplify attaching it). We only
  // show the modal when the server actually returns 401 or token-expired (response interceptor).
  axios.interceptors.request.use(
    async (config) => {
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  console.log('✅ Axios interceptors setup complete');
};
